import React from "react";
//#region types
import { CurrencyEnums } from "models/enums";
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { KeyValue } from "models/utilTypes/keyValue";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DateConstants } from "utils/constants";
//#endregion
//#region components
import { DetailsTableViewSmall, TitledContainerInner, EntityCardContent, Skeletons } from "components/common";
import {
  PersonSalaryFooter,
  PersonSalaryPendingApprovalList,
  useOnSalaryPaymentViewInfo,
  SalaryReportWarnings,
  LargeSalaryWarning,
} from "components/personSalary";
import TopTodoCard from "../../_partials/topTodoCard";
import { Skeleton } from "@mui/material";
import { useSalaryService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";

const SalaryPaymentReportTodo: React.FC<TodoCardProps> = (props) => {
  const salaryService = useSalaryService();
  const salaryPayments = props.item.personSalaryReport;
  const dueDate = DateHelper.formatDate(props.item.date);
  const latestMonth = LocalizationHelperInstance.getFormattedDate(
    new Date(props.item.date),
    DateConstants.MonthLongConfig
  );

  const onViewInfoClick = useOnSalaryPaymentViewInfo();

  const [selectedRows, setSelectedRows] = React.useState<WintSuperkollAPIModel.PersonSalaryEntryApprovalItem[]>([]);
  const [hasLargeSalaries, setHasLargeSalaries] = React.useState<boolean>(false);
  const [personSalaryReport, setPersonSalaryReport] = React.useState<WintSuperkollAPIModel.PersonSalaryReport>();
  const [loading, setLoading] = React.useState(false);
  const [updatingAmounts, setUpdatingAmounts] = React.useState(false);

  React.useEffect(() => {
    setPersonSalaryReport(
      salaryPayments ? { ...salaryPayments, rows: salaryPayments.rows?.map((r) => ({ ...r })) } : undefined
    );
    setHasLargeSalaries(getHasLargeSalaries(salaryPayments?.rows));
  }, [salaryPayments]);

  const onSalaryEntriesSelect = (rows: WintSuperkollAPIModel.PersonSalaryRow[]) => {
    setSelectedRows(rows.map((r) => ({ id: r.id, amount: r.monthlySalary ?? 0 })));
  };

  const getHasLargeSalaries = (rows?: WintSuperkollAPIModel.PersonSalaryRow[]) =>
    rows?.find((r) => r.monthlySalary && r.monthlySalary >= 100000) !== undefined;

  const reloadTodo = (onSuccess?: (item: WintSuperkollAPIModel.PersonSalaryReport) => void) => {
    setSelectedRows([]);
    if (personSalaryReport) {
      setLoading(true);

      salaryService
        .getPersonSalaryReport()
        .then((result) => {
          onSuccess && onSuccess(result);
          props.onActionSuccess();
        })
        .finally(() => setLoading(false));
    }
  };

  const onMonthlySalaryChange = (salaryId: number, personId: number, value?: number) => {
    const selected = [...selectedRows];
    const selectedRecord = selected.find((s) => s.id === salaryId);
    if (selectedRecord) selectedRecord.amount = value ?? 0;
    setSelectedRows(selected);

    setUpdatingAmounts(true);
    salaryService.getUpdatedRow(salaryId, personId, value ?? 0).then((result) => {
      const rows = personSalaryReport?.rows ? [...personSalaryReport.rows] : [];
      const toUpdateRowIndex = rows.findIndex((r) => r.id === salaryId);

      const prevRow = rows[toUpdateRowIndex];
      const salary = prevRow.monthlySalary;
      const employeeContribution = prevRow.employerContributionTax;

      rows.splice(toUpdateRowIndex, 1, result);

      setHasLargeSalaries(getHasLargeSalaries(rows));

      if (personSalaryReport) {
        const updatedReport = {
          ...personSalaryReport,
          totalSalary: personSalaryReport?.totalSalary - (salary ?? 0) + (result.monthlySalary ?? 0),
          totalEmployerContribution:
            personSalaryReport.totalEmployerContribution -
            (employeeContribution ?? 0) +
            (result.employerContributionTax ?? 0),
          rows: rows,
        };
        setPersonSalaryReport(updatedReport);
      }

      setUpdatingAmounts(false);
    });
  };

  const totalAmount = personSalaryReport
    ? personSalaryReport.totalSalary + personSalaryReport.totalEmployerContribution + personSalaryReport.totalBenefits
    : 0;

  const getMinifiedTableData = () => {
    const data = [
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.Salaries),
        updatingAmounts ? (
          <Skeleton width="50%" height="20px" />
        ) : (
          LocalizationHelperInstance.getFormattedCurrency(
            personSalaryReport!.totalSalary,
            CurrencyEnums.CurrencyCode.SEK
          )
        )
      ),
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.EmployerContribution),
        updatingAmounts ? (
          <Skeleton width="50%" height="20px" />
        ) : (
          LocalizationHelperInstance.getFormattedCurrency(
            personSalaryReport!.totalEmployerContribution,
            CurrencyEnums.CurrencyCode.SEK
          )
        )
      ),
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.Benefits),
        updatingAmounts ? (
          <Skeleton width="50%" height="20px" />
        ) : (
          LocalizationHelperInstance.getFormattedCurrency(
            personSalaryReport!.totalBenefits,
            CurrencyEnums.CurrencyCode.SEK
          )
        )
      ),
    ];
    return data;
  };

  const isAutoCreatedSimpleSalary = salaryService.isAutoCreatedSimpleSalary();

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={
            updatingAmounts ? (
              <Skeletons.TopCardDescriptionOneSkeleton />
            ) : (
              LocalizationHelperInstance.getFormattedCurrency(totalAmount, "SEK")
            )
          }
          descriptionHeadingTwo={latestMonth.toString()}
          footerText={`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Latest)}: ${dueDate} `}
          onViewAllClick={onViewInfoClick}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.TodoSelectionPanelTitle)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(
            isAutoCreatedSimpleSalary
              ? TranslationKeys.Salary.TodoSelectionPanelSubTitleSalaryAuto
              : TranslationKeys.Salary.TodoSelectionPanelSubTitle
          )}
        >
          <DetailsTableViewSmall hideButton={false} detailsList={personSalaryReport ? getMinifiedTableData() : []} />
        </TitledContainerInner>
      }
      infoViewer={
        <PersonSalaryPendingApprovalList
          rows={
            personSalaryReport
              ? personSalaryReport.rows?.map((r) => ({ ...r, select: selectedRows.some((s) => s.id === r.id) })) ?? []
              : undefined
          }
          isAutoCreatedSimpleSalary={isAutoCreatedSimpleSalary}
          onSalaryEntriesSelect={onSalaryEntriesSelect}
          onMonthlySalaryChange={onMonthlySalaryChange}
          checkBoxesCheckedAsDefault={true}
        />
      }
      footer={<PersonSalaryFooter selected={selectedRows ?? []} reloadHandler={reloadTodo} disableSuccessMessages />}
      loading={loading}
    >
      <SalaryReportWarnings personSalaryReport={personSalaryReport} />
      <LargeSalaryWarning hasLargeSalaries={hasLargeSalaries} />
    </EntityCardContent>
  );
};

export default SalaryPaymentReportTodo;
